import { drawCableTopology } from './wireTurns';
import type * as PIXI from 'pixi.js';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { Side } from '@/schemas/tiles';

export const bundleWireColor = 0xff0000;
export const bundleLowWireColor = 0x590000;

/** Anonymous scalar graph nodes are not bundle labels, even when lane arrays exist. */
export function carriesBundleSignals(
  tile: Pick<TileInstance, 'meta' | 'laneSignalLabels'>,
): boolean {
  return Boolean(
    tile.meta.bundle ||
      Object.values(tile.laneSignalLabels ?? {}).some((labels) =>
        labels?.some((label) => label.length > 0),
      ),
  );
}

/** Fixed activity levels keep a single live signal visible in arbitrarily large nets. */
export const cableActivityAlpha = (activity: number): number =>
  activity <= 0 ? 0.35 : activity >= 1 ? 1 : 0.7;

/** Opaque tones avoid alpha accumulation where wire arms and the hub overlap. */
export function cableActivityColor(activity: number, error = false): number {
  const level = cableActivityAlpha(activity);
  const base = error ? 0xff4444 : bundleWireColor;
  const channel = (shift: number) => Math.round(((base >> shift) & 0xff) * level);
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

/** A five-pixel cable with a one-pixel inset stripe, continuous at every edge. */
export function drawCable(
  g: PIXI.Graphics,
  connections: Partial<Record<Side, boolean>>,
  activity = 0,
  error = false,
) {
  const color = cableActivityColor(activity, error);
  drawCableTopology(g, connections, color);
}

/** The cable artwork is a quantized activity tone plus connected arms and errors. */
export function bundleCableArtwork(
  tile: {
    meta: { bundle?: { sides?: Partial<Record<Side, readonly number[]>> } };
    laneOutputs?: Partial<Record<Side, readonly number[]>>;
    laneInputs?: Partial<Record<Side, readonly number[]>>;
    bundleErrors: readonly unknown[];
  },
  connections: Partial<Record<Side, boolean>>,
) {
  const values = new Map<number, boolean>();
  for (const side of Object.values(Side)) {
    const mapping = tile.meta.bundle?.sides?.[side];
    const observed = tile.laneOutputs?.[side] ?? tile.laneInputs?.[side] ?? [];
    observed.forEach((value, slot) => {
      const lane = mapping?.[slot] ?? slot;
      values.set(lane, Boolean(values.get(lane) || value));
    });
  }
  const activity = values.size
    ? Array.from(values.values()).filter(Boolean).length / values.size
    : 0;
  const connected = { ...connections };
  for (const side of Object.values(Side))
    if (tile.meta.bundle?.sides?.[side]?.length === 0) connected[side] = false;
  return { activity, connections: connected, error: tile.bundleErrors.length > 0 };
}

export function drawBundle(
  g: PIXI.Graphics,
  tile: TileInstance,
  connections: Partial<Record<Side, boolean>>,
  _width: number,
) {
  const artwork = bundleCableArtwork(tile, connections);
  drawCable(g, artwork.connections, artwork.activity, artwork.error);
}
